import type { BinScope } from './bin-scope';
import { authHeader } from './auth';

const API_BASE = 'https://esl-api.megafood.dk/api';

// ---------------------------------------------------------------------------
// Interfaces
// ---------------------------------------------------------------------------

export interface CorridorSummary {
  aisle: number;
  sides: {
    zone_code: string;
    zone_prefix: string;
    total_bins: number;
    occupied_bins: number;
    missing_items: number;
  }[];
}

export interface CorridorBin {
  code: string;
  zone_code: string;
  bin_type_code: string;
  aisle: number;
  bay: number;
  level: number;
  position: number | null;
  is_empty: boolean;
  item_count: number;
  has_missing: boolean;
  /** Raf satış değeri (stok x satış fiyatı, kr). */
  sales_value: number;
  /** Depo geneli, bölge bazlı ısı kademesi: 0 medyan altı, 1 medyan..ilk %10, 2 ilk %10, 3 ilk %3. */
  value_tier: number;
}

/** Raf değeri kısa yazımı: `850 kr`, `12,4k kr`, `110k kr`, `1,2M kr`. */
export function formatKr(v: number): string {
  const oneDecimal = (x: number) => x.toFixed(1).replace('.', ',');
  if (v < 1000) return `${Math.round(v)} kr`;
  if (v < 100000) return `${oneDecimal(v / 1000)}k kr`;
  if (v < 1000000) return `${Math.round(v / 1000)}k kr`;
  return `${oneDecimal(v / 1000000)}M kr`;
}

export interface CorridorDetail {
  aisle: number;
  sides: {
    zone: string;
    prefix: string;
    maxBay: number;
    minBay: number;
    maxLevel: number;
    minLevel: number;
    bins: CorridorBin[];
  }[];
}

export interface BinInventoryItem {
  item_id: string;
  description: string;
  brand: string;
  category: string;
  sub_category: string;
  lot_no: string;
  qty_base: number;
  sales_uom: string;
  base_uom: string;
  qty_per_uom: number;
  uom_description: string;
  unit_price: number;
  total_price: number;
  qty_sales: number;
  qty_broken: number;
  display_qty: string;
}

export interface BinAssignment {
  id: number;
  item_id: string;
  description: string;
  in_stock: boolean;
  sequence?: number;
  itemKey?: string;
  eslDisplays?: { serial: string; placement: number; columns: number; layoutName: string }[];
}

export interface OtherBinLocation {
  item_id: string;
  bin_code: string;
  zone_code: string;
  qty_base: number;
  assigned: boolean;
  /** Bin envanter sınıfı (bkz. ADR-0009); eski yanıtlarda yok. */
  binScope?: BinScope;
}

export interface OrphanEsl {
  key: string;
  displays: { serial: string; placement: number; columns: number; layoutName: string }[];
}

export interface BinDetail {
  code: string;
  zone_code: string;
  bin_type_code: string;
  bin_ranking: number;
  inventory: BinInventoryItem[];
  assignments: BinAssignment[];
  otherBins: OtherBinLocation[];
  orphanEsl?: OrphanEsl[];
}

export interface SearchResult {
  item_id: string;
  description: string;
  brand: string;
  category: string;
  sub_category: string;
  bins: {
    bin_code: string;
    zone_code: string;
    aisle: number | null;
    qty_base: number;
    sales_uom: string;
    qty_per_uom: number;
    binScope?: BinScope;
  }[];
}

export interface MissingItem {
  bin_code: string;
  item_id: string;
  description: string;
  brand: string;
  category: string;
  zone_code: string;
  aisle: number | null;
  bay: number | null;
}

export interface MissingReport {
  total: number;
  corridors: {
    aisle: number | null;
    zone: string;
    items: MissingItem[];
  }[];
}

export interface SyncStatus {
  sync_type: string;
  started_at: string;
  completed_at: string | null;
  records: number;
  status: string;
}

export interface SpecialBin {
  code: string;
  zone_code: string;
  bin_type_code: string;
  item_count: number;
  total_qty: number;
}

export interface AssignmentItem {
  id: number;
  bin_code: string;
  item_id: string;
  description: string;
  brand: string;
  category: string;
  assigned_at: string;
  assigned_by: string;
  zone_code: string;
  aisle: number | null;
}

export interface UnassignedItem {
  item_id: string;
  description: string;
  brand: string;
  category: string;
  bin_count: number;
  total_qty: number;
}

// ---------------------------------------------------------------------------
// Helper
// ---------------------------------------------------------------------------

async function api<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, options);
  if (!res.ok) {
    throw new Error(`API error ${res.status}: ${res.statusText}`);
  }
  return res.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// Fetch functions
// ---------------------------------------------------------------------------

export function fetchCorridors(): Promise<CorridorSummary[]> {
  return api<CorridorSummary[]>('/layout/corridors');
}

export function fetchCorridor(aisle: number): Promise<CorridorDetail> {
  return api<CorridorDetail>(`/layout/corridor/${aisle}`);
}

export function fetchBin(code: string): Promise<BinDetail> {
  return api<BinDetail>(`/layout/bin/${code}`);
}

export function searchItems(q: string): Promise<SearchResult[]> {
  return api<SearchResult[]>(`/layout/search?q=${encodeURIComponent(q)}`);
}

export interface CorridorItemsResponse {
  items: {
    bin_code: string; zone_code: string; item_id: string; description: string;
    brand: string; category: string; qty_base: number;
    sales_uom: string; base_uom: string; qty_per_uom: number;
    unit_price: number; assigned: number; assignment_id: number | null;
  }[];
  missing: {
    bin_code: string; zone_code: string; item_id: string; description: string;
    brand: string; category: string; assignment_id: number;
  }[];
  otherBins: OtherBinLocation[];
}

export function fetchCorridorItems(aisle: number, zone?: string): Promise<CorridorItemsResponse> {
  const params = zone ? `?zone=${encodeURIComponent(zone)}` : '';
  return api<CorridorItemsResponse>(`/layout/corridor/${aisle}/items${params}`);
}

export function fetchMissing(aisle?: number): Promise<MissingReport> {
  const path = aisle != null ? `/missing/corridor/${aisle}` : '/missing';
  return api<MissingReport>(path);
}

export function fetchSpecialBins(): Promise<SpecialBin[]> {
  return api<SpecialBin[]>('/layout/special-bins');
}

export function fetchSyncStatus(): Promise<SyncStatus[]> {
  return api<SyncStatus[]>('/sync/status');
}

// Sync uç noktaları girişli kullanıcının token'ını ister (#27).
export async function triggerSync(type: 'inventory' | 'binlist'): Promise<any> {
  return api<any>(`/sync/${type}`, { method: 'POST', headers: await authHeader() });
}

export function fetchAssignments(binCode?: string): Promise<AssignmentItem[]> {
  const path = binCode ? `/assignments/bin/${binCode}` : '/assignments';
  return api<AssignmentItem[]>(path);
}

export function fetchUnassignedItems(): Promise<UnassignedItem[]> {
  return api<UnassignedItem[]>('/assignments?unassigned=true');
}

export async function createAssignment(bin_code: string, item_id: string): Promise<void> {
  await api<void>('/assignments', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ bin_code, item_id }),
  });
}

export async function deleteAssignment(id: number): Promise<void> {
  await api<void>(`/assignments/${id}`, { method: 'DELETE' });
}

// ---------------------------------------------------------------------------
// ESL Coverage
// ---------------------------------------------------------------------------

export interface BinEslCoverage {
  totalKeys: number;
  boundKeys: number;
  problems: string[];
  missingLabel: boolean;
  inventoryStatus: 'none' | 'low' | 'normal';
  displayFault: ('comm' | 'battery')[] | null;
}

export interface EslCoverageResponse {
  coverage: Record<string, BinEslCoverage>;
  totalDisplays: number;
}

export function fetchEslCoverage(): Promise<EslCoverageResponse> {
  return api<EslCoverageResponse>('/layout/esl-coverage');
}
